import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    CircularProgress,
    IconButton
} from '@mui/material';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSnackbar } from "../../../utils/useSnackbar";
import { breakpoints } from '../../../theme/theme';
import styles from '../../../styles/login/rightSection.module.scss';
import { EmailTemplateKeys } from '../../../enums/EmailTemplatesEnum';
import { requestPasswordReset } from '../../../utils/api/user_apis';
import { STATUS } from '../../../utils/api/sharedfunction';

// Validation schema for email only
const ForgotPasswordSchema = z.object({
    email: z.string().email("Please enter a valid email address").min(1, "Email is required"),
});

const ForgotPasswordForm = ({ onBack }) => {
    const [loading, setLoading] = useState(false);
    const { showSnackbar } = useSnackbar();

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm({
        mode: "onChange",
        resolver: zodResolver(ForgotPasswordSchema),
        defaultValues: { email: "" },
    });

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const response = await requestPasswordReset({
                email: data.email.trim(),
                type: EmailTemplateKeys.RESET_PASSWORD,
            });

            if (response.status === STATUS.SUCCESS) {
                // Success response
                showSnackbar(response.message || 'Password reset link has been sent to your email address.', 'success');
                reset();
                
                // Go back to login after success
                setTimeout(() => {
                    onBack();
                }, 3000); // Increased to 3 seconds to give user time to read the message
            } else {
                showSnackbar(response.error || 'Failed to send reset email. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Password reset request error:', error);
            showSnackbar(error.error || 'Failed to send reset email. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Box className={styles['form-header']}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <IconButton 
                        onClick={onBack}
                        sx={{ 
                            p: 0.5, 
                            color: '#64748b',
                            '&:hover': { 
                                color: '#3b82f6',
                                backgroundColor: 'rgba(59, 130, 246, 0.04)'
                            }
                        }}
                    >
                        <ArrowLeftIcon style={{ width: 20, height: 20 }} />
                    </IconButton>
                </Box>
                <h6 className={styles['form-title']}>
                    Reset your password
                </h6>
                <p className={styles['form-subtitle']}>
                    Enter your email address and we'll send you a link to reset your password
                </p>
            </Box>
            <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                className={styles['form']}
            >
                <Box className={styles['form-field']}>
                    <h6
                        htmlFor="email"
                        className={styles['field-label']}
                    >
                        Email address
                    </h6>

                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                id="email"
                                type="email"
                                placeholder="Enter your email address"
                                variant="outlined"
                                fullWidth
                                size="small"
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                autoFocus
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '0.5rem',
                                        bgcolor: '#fff',
                                        fontSize: '1rem',
                                        color: '#0f172a',
                                        transition: 'all 0.2s',
                                        boxShadow: 'none',
                                        border: 'none',
                                        '& fieldset': {
                                            borderRadius: '0.5rem',
                                            borderColor: '#CBD5E1',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#3b82f6',
                                            borderRadius: '0.5rem',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3b82f6',
                                            borderWidth: 2,
                                            borderRadius: '0.5rem',
                                        },
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#CBD5E1',
                                        borderWidth: 1,
                                        borderRadius: '0.5rem',
                                        transition: 'border-color 0.2s, border-width 0.2s, border-radius 0.2s',
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        height: '1.2rem',
                                        [`@media (min-width:${breakpoints.xl}px)`]: {
                                            height: '1.625rem',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '12px 16px',
                                        backgroundColor: '#fff',
                                        fontSize: '0.7rem',
                                        [`@media (min-width:${breakpoints.xl}px)`]: {
                                            fontSize: '1rem',
                                        },
                                        color: '#0f172a',
                                        lineHeight: '1.5rem',
                                        fontWeight: 400,
                                        transition: 'all 0.2s',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: '#64748b',
                                        opacity: 1,
                                        fontSize: '13px',
                                        lineHeight: '1rem',
                                        fontWeight: 400,
                                    },  
                                    '& .MuiFormHelperText-root': {
                                        fontSize: '0.7rem',
                                        lineHeight: '1rem',
                                        color: '#ef4444',
                                    },
                                }}
                            />
                        )}
                    />
                </Box>

                <Button
                    type="submit"
                    variant="primaryBtn"
                    fullWidth
                    disabled={loading}
                    className={styles['submit-button']}
                >
                    {loading ? (
                        <CircularProgress 
                            size={20} 
                            className={styles['submit-button-loading']}
                        />
                    ) : (
                        'Send reset link'
                    )}
                </Button>

                <Button
                    type="button"
                    variant="text"
                    fullWidth
                    onClick={onBack}
                    disabled={loading}
                    sx={{
                        mt: 1,
                        color: '#64748b',
                        fontSize: '0.75rem',
                        fontWeight: 400,
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: 'rgba(100, 116, 139, 0.04)',
                            color: '#475569',
                        },
                        [`@media (min-width:${breakpoints.xl}px)`]: {
                            fontSize: '0.875rem',
                        },
                    }}
                >
                    Back to sign in
                </Button>
            </Box>
        </>
    );
};

export default ForgotPasswordForm;
